import { useState, useRef } from 'react';
import axios from 'axios';
import { Mail, Send, CheckCircle, MapPin, Clock, Euro, Bell, Home } from 'lucide-react';

export default function ComingSoon() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [done, setDone] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const formStartedAt = useRef(Date.now());

  // Aanbieder-interesse — toont extra velden als de gebruiker geld wil verdienen
  const [wantsToHost, setWantsToHost] = useState(false);
  const [hostName, setHostName] = useState('');
  const [hostCity, setHostCity] = useState('');
  const [hostPostcode, setHostPostcode] = useState('');
  const [hostLocationDesc, setHostLocationDesc] = useState('');
  const [hostMessage, setHostMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (!email.trim()) {
      setStatus({ kind: 'error', text: 'Voer een e-mailadres in' });
      return;
    }
    if (!agreed) {
      setStatus({ kind: 'error', text: 'Ga akkoord met onze voorwaarden om door te gaan' });
      return;
    }
    if (wantsToHost && !hostCity.trim()) {
      setStatus({ kind: 'error', text: 'Vul je stad in zodat we weten waar je toilet staat' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/newsletter/subscribe`, {
        email: email.trim(),
        source: 'coming-soon',
        website: honeypot,
        form_started_at: formStartedAt.current,
        wants_to_host: wantsToHost,
        host_name: hostName,
        host_city: hostCity,
        host_postcode: hostPostcode,
        host_location_desc: hostLocationDesc,
        host_message: hostMessage,
      });
      setDone(true);
    } catch (err) {
      setStatus({ kind: 'error', text: err.response?.data?.error || 'Aanmelden mislukt' });
    }
    setSubmitting(false);
  };

  return (
    <div style={styles.page}>
      <div style={styles.bgBlob1} />
      <div style={styles.bgBlob2} />

      <div style={styles.container}>
        <div style={styles.logoRow}>
          <img src="/images/LOGO.svg" alt="SpotjeWcPotje" style={styles.logo} />
        </div>

        <div style={styles.badge}>
          <span style={styles.badgeDot} />
          <span style={styles.badgeText}>Binnenkort live</span>
        </div>

        <h1 style={styles.heading}>
          Nooit meer zoeken<br />
          <span style={styles.headingAccent}>naar een toilet</span>
        </h1>

        <p style={styles.lead}>
          SpotjeWcPotje verbindt mensen die een toilet zoeken met mensen die hun toilet openstellen.
          Schrijf je in en je hoort als eerste wanneer je de app kunt downloaden.
        </p>

        {done ? (
          <div style={styles.successCard}>
            <div style={styles.successIconWrap}>
              <CheckCircle size={32} color="white" strokeWidth={2.5} />
            </div>
            <h2 style={styles.successTitle}>
              {wantsToHost ? 'Bedankt voor je interesse!' : 'Je staat op de lijst!'}
            </h2>
            <p style={styles.successText}>
              {wantsToHost
                ? 'We sturen je een berichtje zodra de app live gaat — en eerder als we vragen hebben over jouw locatie.'
                : 'Bedankt voor je aanmelding. We sturen je een berichtje zodra SpotjeWcPotje live gaat.'}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} style={styles.formCard}>
            {/* Honeypot — onzichtbaar voor mensen, bots vullen 'm vaak in */}
            <input
              type="text"
              name="website"
              tabIndex="-1"
              autoComplete="off"
              value={honeypot}
              onChange={e => setHoneypot(e.target.value)}
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
              aria-hidden="true"
            />
            <div style={styles.formHeader}>
              <div style={styles.formIconWrap}>
                <Bell size={20} color="white" strokeWidth={2.4} />
              </div>
              <div>
                <h2 style={styles.formTitle}>Laat je e-mail achter</h2>
                <p style={styles.formSubtitle}>
                  Dan krijg je één keer een berichtje, op het moment dat de app live gaat.
                </p>
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.inputWrap}>
                <Mail size={18} color="#94a3b8" style={styles.inputIcon} />
                <input
                  type="email"
                  placeholder="jouw@email.nl"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  style={styles.input}
                  required
                />
              </div>
              <button
                type="submit"
                style={{ ...styles.submitBtn, opacity: (submitting || !agreed) ? 0.5 : 1 }}
                disabled={submitting || !agreed}
              >
                <Send size={15} />
                <span>{submitting ? 'Bezig...' : 'Hou mij op de hoogte'}</span>
              </button>
            </div>

            {/* Aanbieder-opt-in: groene call-out die opent als checkbox aan staat */}
            <div style={{
              borderRadius: '14px',
              border: '1px solid ' + (wantsToHost ? '#86efac' : '#e2e8f0'),
              background: wantsToHost ? 'linear-gradient(135deg, #f0fdf4, #ecfdf5)' : '#f8fafc',
              padding: '14px',
              transition: 'all 0.2s',
            }}>
              <label style={{ ...styles.agreeRow, marginTop: 0, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={wantsToHost}
                  onChange={e => setWantsToHost(e.target.checked)}
                  style={{ ...styles.agreeCheckbox, accentColor: '#16a34a' }}
                />
                <span style={{ ...styles.agreeText, color: '#0f172a' }}>
                  <strong>Ik wil ook geld verdienen met mijn toilet</strong>
                  <span style={{ display: 'block', fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                    Woon jij in een drukke buurt, vlakbij stations, evenementen of horeca? Verdien wat bij door je toilet open te stellen — wij nemen contact op zodra we live gaan.
                  </span>
                </span>
              </label>

              {wantsToHost && (
                <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <input
                    type="text"
                    placeholder="Je voornaam (optioneel)"
                    value={hostName}
                    onChange={e => setHostName(e.target.value)}
                    maxLength={120}
                    style={{ ...styles.input, padding: '12px 14px' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      placeholder="Stad *"
                      value={hostCity}
                      onChange={e => setHostCity(e.target.value)}
                      maxLength={80}
                      required
                      style={{ ...styles.input, padding: '12px 14px', flex: '2 1 160px' }}
                    />
                    <input
                      type="text"
                      placeholder="Postcode (optioneel)"
                      value={hostPostcode}
                      onChange={e => setHostPostcode(e.target.value)}
                      maxLength={10}
                      style={{ ...styles.input, padding: '12px 14px', flex: '1 1 120px' }}
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Wat maakt jouw locatie interessant? (bv. centrum, vlakbij station)"
                    value={hostLocationDesc}
                    onChange={e => setHostLocationDesc(e.target.value)}
                    maxLength={300}
                    style={{ ...styles.input, padding: '12px 14px' }}
                  />
                  <textarea
                    placeholder="Heb je vragen of bijzonderheden? (optioneel)"
                    value={hostMessage}
                    onChange={e => setHostMessage(e.target.value)}
                    maxLength={600}
                    rows={2}
                    style={{ ...styles.input, padding: '12px 14px', resize: 'vertical', minHeight: '60px', fontFamily: 'inherit' }}
                  />
                </div>
              )}
            </div>

            <label style={styles.agreeRow}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                style={styles.agreeCheckbox}
              />
              <span style={styles.agreeText}>
                Ik ga akkoord met de{' '}
                <a href="/voorwaarden" style={styles.link}>voorwaarden</a>
                {' '}en{' '}
                <a href="/privacy" style={styles.link}>privacyverklaring</a>.
              </span>
            </label>

            {status && (
              <p style={{
                margin: 0,
                fontSize: '13px',
                fontWeight: 600,
                color: status.kind === 'success' ? '#16a34a' : '#dc2626',
              }}>
                {status.text}
              </p>
            )}
          </form>
        )}

        <div style={styles.featureRow}>
          <div style={styles.feature}>
            <div style={{ ...styles.featureIcon, background: '#eff6ff', color: '#3b82f6' }}>
              <MapPin size={18} />
            </div>
            <div style={styles.featureText}>
              <span style={styles.featureTitle}>Toiletten op de kaart</span>
              <span style={styles.featureSub}>Vind direct een beschikbaar toilet in de buurt.</span>
            </div>
          </div>
          <div style={styles.feature}>
            <div style={{ ...styles.featureIcon, background: '#fef3c7', color: '#f59e0b' }}>
              <Clock size={18} />
            </div>
            <div style={styles.featureText}>
              <span style={styles.featureTitle}>Snel geregeld</span>
              <span style={styles.featureSub}>Reserveer in seconden, betaal via iDEAL.</span>
            </div>
          </div>
          <div style={styles.feature}>
            <div style={{ ...styles.featureIcon, background: '#dcfce7', color: '#22c55e' }}>
              <Euro size={18} />
            </div>
            <div style={styles.featureText}>
              <span style={styles.featureTitle}>Verdien geld</span>
              <span style={styles.featureSub}>Stel je toilet open en verdien per gebruik.</span>
            </div>
          </div>
        </div>

        <div style={styles.footer}>
          <p style={styles.footerText}>© {new Date().getFullYear()} SpotjeWcPotje</p>
          <div style={styles.footerLinks}>
            <a href="mailto:info@spotjewcpotje.nl" style={styles.footerLink}>info@spotjewcpotje.nl</a>
            <a href="/privacy" style={styles.footerLink}>Privacy</a>
            <a href="/voorwaarden" style={styles.footerLink}>Voorwaarden</a>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    position: 'relative',
    minHeight: '100vh',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 50%, #eff6ff 100%)',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '48px 16px',
    boxSizing: 'border-box',
  },
  bgBlob1: {
    position: 'absolute',
    top: '-160px', right: '-160px',
    width: '480px', height: '480px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(59,130,246,0.18), transparent 70%)',
    pointerEvents: 'none',
  },
  bgBlob2: {
    position: 'absolute',
    bottom: '-200px', left: '-160px',
    width: '520px', height: '520px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(99,102,241,0.14), transparent 70%)',
    pointerEvents: 'none',
  },

  container: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxWidth: '560px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },

  logoRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    margin: '0 auto 4px',
    maxWidth: '100%',
  },
  beeldmerk: { height: '44px', width: 'auto', maxWidth: '100%' },
  logo: { height: '64px', width: 'auto', maxWidth: '100%' },

  countdown: { marginTop: '20px', marginBottom: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' },
  countdownLabel: { fontSize: '13px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#64748b' },
  countdownRow: { display: 'flex', alignItems: 'flex-start', gap: '4px', flexWrap: 'wrap', justifyContent: 'center' },
  countdownCellWrap: { display: 'flex', alignItems: 'flex-start' },
  countdownCell: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    minWidth: '74px', padding: '14px 12px',
    background: 'rgba(255, 255, 255, 0.75)', backdropFilter: 'blur(8px)',
    border: '1px solid rgba(15, 23, 42, 0.08)',
    borderRadius: '14px',
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
  },
  countdownValue: { fontSize: '34px', fontWeight: 800, color: '#0f172a', lineHeight: 1, fontVariantNumeric: 'tabular-nums' },
  countdownUnit: { marginTop: '6px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#94a3b8' },
  countdownSep: { fontSize: '34px', fontWeight: 800, color: '#cbd5e1', lineHeight: 1, padding: '14px 4px 0' },
  brand: { fontSize: '20px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.3px' },

  badge: {
    display: 'inline-flex',
    alignSelf: 'center',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 14px',
    borderRadius: '999px',
    background: 'rgba(59,130,246,0.10)',
    border: '1px solid rgba(59,130,246,0.25)',
  },
  badgeDot: {
    width: '8px', height: '8px',
    borderRadius: '50%',
    background: '#3b82f6',
    boxShadow: '0 0 0 4px rgba(59,130,246,0.18)',
  },
  badgeText: { fontSize: '12px', fontWeight: 700, color: '#1d4ed8', letterSpacing: '0.2px' },

  heading: {
    margin: 0,
    fontSize: 'clamp(34px, 6vw, 52px)',
    fontWeight: 800,
    letterSpacing: '-1.2px',
    lineHeight: 1.05,
    color: '#0f172a',
  },
  headingAccent: {
    background: 'linear-gradient(135deg, #2563eb, #6366f1)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },

  lead: {
    margin: '0 auto',
    maxWidth: '460px',
    fontSize: '16px',
    lineHeight: 1.65,
    color: '#475569',
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    maxWidth: '440px',
    margin: '0 auto',
    alignItems: 'center',
  },
  formCard: {
    display: 'flex', flexDirection: 'column', gap: '14px',
    width: '100%', maxWidth: '520px', margin: '0 auto',
    background: 'white',
    border: '1px solid rgba(15,23,42,0.08)',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 10px 36px rgba(15, 23, 42, 0.08)',
    textAlign: 'left',
  },
  formHeader: { display: 'flex', alignItems: 'flex-start', gap: '14px' },
  formIconWrap: {
    width: '44px', height: '44px', borderRadius: '12px',
    background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 6px 16px rgba(37, 99, 235, 0.25)',
  },
  formTitle: { margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a', lineHeight: 1.25 },
  formSubtitle: { margin: '4px 0 0', fontSize: '13px', color: '#64748b', lineHeight: 1.5 },
  formRow: { display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px' },
  formBenefits: { margin: '4px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' },
  formBenefit: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#475569', lineHeight: 1.4 },
  agreeRow: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    cursor: 'pointer', userSelect: 'none', marginTop: '4px',
  },
  agreeCheckbox: {
    width: '18px', height: '18px', marginTop: '2px',
    accentColor: '#2563eb', cursor: 'pointer', flexShrink: 0,
  },
  agreeText: { fontSize: '13px', color: '#475569', lineHeight: 1.5 },
  inputWrap: { position: 'relative', width: '100%' },
  inputIcon: { position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' },
  input: {
    width: '100%',
    padding: '16px 16px 16px 48px',
    borderRadius: '14px',
    border: '1px solid #e2e8f0',
    fontSize: '15px',
    color: '#0f172a',
    fontFamily: 'inherit',
    outline: 'none',
    background: 'white',
    boxShadow: '0 1px 4px rgba(15,23,42,0.04)',
    boxSizing: 'border-box',
  },
  submitBtn: {
    width: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '16px 22px',
    borderRadius: '14px',
    background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '15px',
    fontFamily: 'inherit',
    boxShadow: '0 8px 24px rgba(37,99,235,0.28)',
    letterSpacing: '0.1px',
  },
  privacy: {
    margin: '4px 0 0',
    fontSize: '11px',
    color: '#94a3b8',
    lineHeight: 1.55,
    maxWidth: '420px',
  },
  link: { color: '#3b82f6', textDecoration: 'none' },

  successCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '32px 28px',
    boxShadow: '0 12px 40px rgba(15,23,42,0.08)',
    border: '1px solid #f1f5f9',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    maxWidth: '440px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  successIconWrap: {
    width: '60px', height: '60px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    marginBottom: '4px',
  },
  successTitle: { margin: 0, fontSize: '20px', fontWeight: 800, color: '#0f172a' },
  successText: { margin: 0, fontSize: '14px', color: '#475569', lineHeight: 1.6 },

  featureRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '8px',
    maxWidth: '440px',
    width: '100%',
    margin: '8px auto 0',
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '14px 16px',
    background: 'white',
    border: '1px solid #f1f5f9',
    borderRadius: '14px',
    textAlign: 'left',
  },
  featureIcon: {
    width: '40px', height: '40px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  featureText: { display: 'flex', flexDirection: 'column', gap: '1px' },
  featureTitle: { fontSize: '14px', fontWeight: 700, color: '#0f172a' },
  featureSub: { fontSize: '12px', color: '#64748b', lineHeight: 1.5 },

  footer: {
    marginTop: '24px',
    padding: '20px 0',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerText: { margin: 0, fontSize: '12px', color: '#94a3b8' },
  footerLinks: { display: 'flex', gap: '14px' },
  footerLink: { fontSize: '12px', color: '#64748b', textDecoration: 'none', fontWeight: 600 },
};
